import styled from "styled-components";
import { WorkspaceType } from "@shared/types";
import { CSSProperties, useMemo } from "react";
import { motion } from "motion/react";
import Text from "common/Typography/Text";
import useWorkspace from "utils/hooks/useWorkspace";

interface WorkspaceSwitchProps {
  isShort?: boolean;
  style?: CSSProperties;
}

const WorkspaceSwitch = ({ isShort, style }: WorkspaceSwitchProps) => {
  const { workspace, onWorkspaceChange } = useWorkspace();
  const isDemo = useMemo(() => workspace === WorkspaceType.DEMO, [workspace]);

  if (workspace !== WorkspaceType.USER && workspace !== WorkspaceType.DEMO)
    return null;

  const onSwitchClick = () => {
    if (workspace === WorkspaceType.DEMO) onWorkspaceChange(WorkspaceType.USER);
    else onWorkspaceChange(WorkspaceType.DEMO);
  };

  const selectedText = isDemo ? "Demo Trading" : "Live Trading";
  const unselectedText = isDemo ? "Live" : "Demo";

  return (
    <Background
      onClick={onSwitchClick}
      $isDemo={isDemo}
      $isShort={isShort}
      style={style}
      layout
      //@ts-ignore
      layoutRoot
      transition={{ type: "easeInOut", duration: 0.3 }}
    >
      <WorkspaceText $isDemo={isDemo} $isShort={isShort}>
        {isShort ? unselectedText : `${unselectedText} Trading`}
      </WorkspaceText>
      <WorkspaceBg
        layout
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 250,
        }}
        $isDemo={isDemo}
        $isShort={isShort}
      >
        {selectedText}
      </WorkspaceBg>
    </Background>
  );
};

export default WorkspaceSwitch;

const SWITCH_WIDTH = 148;
const LIVE_BUTTON_WIDTH = 92;
const DEMO_BUTTON_WIDTH = 102;
const PADDING = 4;

const Background = styled(motion.div)<{
  $isDemo?: boolean;
  $isShort?: boolean;
}>`
  display: flex;
  align-items: center;
  background: ${(p) => p.theme.colors.lightBlue};
  cursor: pointer;
  border-radius: 6px;
  height: 32px;
  width: ${(p) => (p.$isShort ? `${SWITCH_WIDTH}px` : "100%")};
  padding: ${PADDING}px;

  ${(p) =>
    p.$isDemo &&
    `
    justify-content: flex-end;
  `};
`;
const WorkspaceBg = styled(motion.div)<{
  $isDemo?: boolean;
  $isShort?: boolean;
}>`
  width: 50%;
  height: 26px;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-align: center;
  color: ${(p) => p.theme.colors.layoutBgColor};
  order: ${(p) => (p.$isDemo ? 2 : 1)};
  background: ${(p) =>
    p.$isDemo
      ? `linear-gradient(114.66deg, ${p.theme.colors.demoTagColors})`
      : p.theme.colors.primaryBlue};
  ${(p) =>
    p.$isShort &&
    `
     width: ${p.$isDemo ? DEMO_BUTTON_WIDTH : LIVE_BUTTON_WIDTH}px;
   `};
`;
const WorkspaceText = styled(Text)<{ $isDemo: boolean; $isShort: boolean }>`
  text-align: center;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  color: ${(p) => p.theme.colors.primaryBlue};
  order: ${(p) => (p.$isDemo ? 1 : 2)};
  line-height: 18px;
  width: 50%;
  ${(p) =>
    p.$isShort &&
    `
      width: ${
        SWITCH_WIDTH -
        (p.$isDemo ? DEMO_BUTTON_WIDTH : LIVE_BUTTON_WIDTH) -
        PADDING * 2
      }px;
    `};
`;
